<template>
    <info-modal ref="successfulRecordedTimesModal" v-model:is-open="isOpen" size="large" body-class="overflow-y-auto" :title-icon="happy" :title="t('timeRecordingSuccess.title', { employeeName })">
        <template #body>
            <table class="w-full border-collapse">
                <thead>
                    <tr>
                        <th class="border-2 border-neutral-500 p-2 sm:p-4 dark:border-neutral-200">
                            <div class="flex items-center"><ion-icon class="mr-2 text-2xl" :icon="logIn" />{{ t("timeRecordingSuccess.tableHeaderColumnStartTime") }}</div>
                        </th>
                        <th class="border-2 border-neutral-500 p-2 sm:p-4 dark:border-neutral-200">
                            <div class="flex items-center"><ion-icon class="mr-2 text-2xl" :icon="logOut" />{{ t("timeRecordingSuccess.tableHeaderColumnEndTime") }}</div>
                        </th>
                        <th v-if="canShowGroupsColumn" class="border-2 border-neutral-500 p-2 sm:p-4 dark:border-neutral-200">
                            <div class="flex items-center"><ion-icon class="mr-2 text-2xl" :icon="pin" />{{ t("timeRecordingSuccess.tableHeaderColumnGroup") }}</div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="recordedAssignment in props.recordedAssignments" :key="recordedAssignment._id">
                        <td v-if="recordedAssignment.startTime" class="border-2 border-neutral-500 p-2 xl:p-4 dark:border-neutral-200">{{ d(recordedAssignment.startTime, "time") }}</td>
                        <td v-else class="border-2 border-neutral-500 p-2 xl:p-4 dark:border-neutral-200">-</td>

                        <td v-if="recordedAssignment.endTime" class="border-2 border-neutral-500 p-2 xl:p-4 dark:border-neutral-200">{{ d(recordedAssignment.endTime, "time") }}</td>
                        <td v-else class="border-2 border-neutral-500 p-2 xl:p-4 dark:border-neutral-200">-</td>

                        <template v-if="canShowGroupsColumn">
                            <td v-if="recordedAssignment.group" class="border-2 border-neutral-500 p-2 xl:p-4 dark:border-neutral-200">{{ getGroupName(recordedAssignment.group) }}</td>
                            <td v-else class="border-2 border-neutral-500 p-2 xl:p-4 dark:border-neutral-200">{{ t("timeRecordingSuccess.noGroup") }}</td>
                        </template>
                    </tr>
                </tbody>
            </table>
            <div v-if="canChangeGroup" class="flex justify-center">
                <button class="rounded-lg border-2 border-neutral-300 px-5 py-2.5 text-center text-sm font-medium dark:text-neutral-100" @click="emit('click-change-group')">
                    {{ t("timeRecordingSuccess.selectOtherGroup") }}
                </button>
            </div>
        </template>
        <template #footer>
            <button class="rounded-lg bg-primary px-5 py-2.5 text-center text-sm font-medium text-neutral-100" @click="isOpen = false">
                {{ t("close") }}
            </button>
        </template>
    </info-modal>
</template>

<script setup lang="ts">
    import InfoModal from "@/components/modals/InfoModal.vue";
    import { BaseGroupDto, RecordedAssignmentWithEmployeeNameDto } from "@/services/api/services";
    import { happy, logIn, logOut, pin } from "ionicons/icons";
    import { computed, useTemplateRef, watch } from "vue";
    import { useI18n } from "vue-i18n";
    import { IonIcon } from "@ionic/vue";
    import { useDeviceStore } from "@/stores/device.store";

    const SECONDS_BEFORE_AUTO_DISMISS: number = 15;
    const { t, d } = useI18n({ useScope: "global" });
    const { deviceConfig } = useDeviceStore();

    const isOpen = defineModel("isOpen", { type: Boolean, required: true });

    const props = defineProps({
        recordedAssignments: {
            type: Array as () => RecordedAssignmentWithEmployeeNameDto[],
            default: () => [],
        },
        groups: {
            type: Array as () => BaseGroupDto[],
            default: () => [],
        },
        canChangeGroup: {
            type: Boolean,
            default: false,
        },
    });
    const emit = defineEmits(["click-change-group"]);

    const employeeName = computed(() => {
        if (!props.recordedAssignments || props.recordedAssignments.length == 0) return undefined;
        // we can take the employee name of the first recorded assignment because all recorded assignments must have the same employee name
        return props.recordedAssignments[0].employeeName;
    });
    const canShowGroupsColumn = computed(() => deviceConfig?.isGroupSelectionAfterClockInEnabled && props.groups.length > 0);

    const getGroupName = (groupId: string) => {
        return props.groups.find((g) => g._id === groupId)?.name ?? t("timeRecordingSuccess.unknownGroup");
    };

    const successfulRecordedTimesModal = useTemplateRef("successfulRecordedTimesModal");
    watch(isOpen, (newValue) => {
        if (newValue) {
            successfulRecordedTimesModal.value?.setAutoDismissAfter({ seconds: SECONDS_BEFORE_AUTO_DISMISS });
        }
    });
</script>
